"use client";

import React, { useRef, useState } from "react";
import { ArrowRight, Globe as GlobeIcon, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const D3Globe = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  return <svg ref={svgRef} className="drop-shadow-[0_0_30px_rgba(var(--color-primary),0.3)]" viewBox="0 0 500 500" style={{
    width: "100%",
    height: "auto",
    maxWidth: "600px"
  }}>
      <defs>
        <radialGradient id="globe-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.2" />
          <stop offset="100%" stopColor="var(--color-secondary)" stopOpacity="0" />
        </radialGradient>
        <filter id="glow-filter">
          <feGaussianBlur stdDeviation="20" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Glow circle */}
      <circle cx="250" cy="250" r="220" fill="url(#globe-glow)" filter="url(#glow-filter)" className="animate-pulse" />

      {/* Base globe */}
      <circle cx="250" cy="250" r="200" fill="var(--color-background)" opacity="0.8" className="animate-[pulse_4s_ease-in-out_infinite]" />

      {/* Graticule lines */}
      <g stroke="var(--color-primary)" strokeWidth="1" fill="none" opacity="0.6">
        {/* Latitude lines */}
        {[0, 30, 60, -30, -60].map(lat => <circle key={`lat-${lat}`} cx="250" cy="250" r={200 * Math.cos(lat * Math.PI / 180)} opacity={0.3} />)}
        {/* Longitude lines */}
        {[0, 45, 90, 135, 180, -45, -90, -135].map(lon => <line key={`lon-${lon}`} x1="250" y1="50" x2="250" y2="450" transform={`rotate(${lon} 250 250)`} opacity={0.3} />)}
      </g>

      {/* Outer border */}
      <circle cx="250" cy="250" r="200" fill="none" stroke="var(--color-secondary)" strokeWidth="2" opacity="0.5" />
    </svg>;
};
const MagneticButton = ({
  children,
  to
}: {
  children: React.ReactNode;
  to: string;
}) => {
  const btnRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!btnRef.current) return;
    const {
      left,
      top,
      width,
      height
    } = btnRef.current.getBoundingClientRect();
    const x = e.clientX - (left + width / 2);
    const y = e.clientY - (top + height / 2);
    const maxMove = 15;
    const moveX = Math.max(-maxMove, Math.min(maxMove, x * 0.2));
    const moveY = Math.max(-maxMove, Math.min(maxMove, y * 0.2));
    setPosition({
      x: moveX,
      y: moveY
    });
  };
  const handleMouseLeave = () => {
    setPosition({
      x: 0,
      y: 0
    });
  };
  return <div data-section-id="16198" ref={btnRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} className="inline-block transition-transform duration-200 ease-out" style={{
    transform: `translate(${position.x}px, ${position.y}px)`
  }}>
      <Button asChild size="lg" className="relative overflow-hidden rounded-lg bg-gradient-to-r from-primary to-cyan-600 text-primary-foreground px-8 py-6 text-lg font-serif font-bold tracking-wide shadow-[0_0_20px_rgba(var(--color-primary),0.3)] hover:shadow-[0_0_40px_rgba(var(--color-primary),0.5)] transition-all duration-300 group border border-white/10">
        <Link to={to}>
          <span className="relative z-10 flex items-center gap-3">
            {children}
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </span>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
        </Link>
      </Button>
    </div>;
};
export default function TurnYourOutdatedSiteIntoADigitalPowerhouse() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="elevate-your-digital-presence" ref={sectionRef} className="relative min-h-[90vh] w-full overflow-hidden bg-background text-foreground dark flex items-center justify-center py-20 lg:py-0">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" />
      
      <div className="absolute top-[-20%] left-[-10%] w-[50vw] h-[50vw] bg-purple-900/20 rounded-full blur-[120px] pointer-events-none mix-blend-screen animate-pulse duration-[5000ms]" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[40vw] h-[40vw] bg-primary/10 rounded-full blur-[100px] pointer-events-none mix-blend-screen" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          <div className={`flex flex-col items-start space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs font-mono uppercase tracking-widest backdrop-blur-md">
              <Sparkles className="w-3 h-3 text-primary" />
              <span>Digital Growth Partner</span>
            </div>

            <div className="relative">
              <h1 className="text-[15vw] lg:text-[10rem] leading-[0.8] font-serif font-bold tracking-tighter select-none">
                <span className="block bg-clip-text text-transparent bg-gradient-to-br from-teal-300 via-cyan-400 to-white animate-[text-shimmer_3s_infinite_linear] bg-[length:200%_auto]">
                  ELEVATE
                </span>
              </h1>
              <div className="absolute -bottom-4 left-0 w-full h-12 bg-gradient-to-t from-primary/20 to-transparent blur-xl opacity-50 pointer-events-none" />
            </div>

            <div className="space-y-6 max-w-xl">
              <h2 className="text-2xl md:text-4xl font-serif font-bold text-white tracking-tight">
                Your Digital Presence into a <span className="text-primary">Strategic Asset</span>
              </h2>
              <p className="text-lg md:text-xl font-sans text-muted-foreground leading-relaxed">
                RPG-Studios crafts high-performance digital ecosystems that accelerate business growth. 
                We believe in building relationships, not just websites, ensuring your brand dominates the modern landscape.
              </p>
            </div>

            <div className="pt-4">
              <MagneticButton to="/ContactPage">
                Start Your Journey
              </MagneticButton>
            </div>

            <div className="grid grid-cols-2 gap-8 pt-8 border-t border-white/10 w-full max-w-md">
              <div>
                <p className="text-3xl font-serif font-bold text-white">95%</p>
                <p className="text-sm font-mono text-muted-foreground uppercase tracking-wider">Client Retention</p>
              </div>
              <div>
                <p className="text-3xl font-serif font-bold text-white">3x</p>
                <p className="text-sm font-mono text-muted-foreground uppercase tracking-wider">Growth Velocity</p>
              </div>
            </div>
          </div>

          <div className={`relative flex items-center justify-center lg:justify-end transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="relative w-full max-w-[500px] aspect-square animate-[float_6s_ease-in-out_infinite]">
              <D3Globe />
              
              <div className="absolute inset-0 border border-primary/5 rounded-full scale-125 animate-[spin_20s_linear_infinite]" />
              <div className="absolute inset-0 border border-dashed border-primary/10 rounded-full scale-150 animate-[spin_30s_linear_infinite_reverse]" />
              
              <div className="absolute top-10 -left-4 md:-left-12 bg-card/40 backdrop-blur-xl border border-white/10 p-4 rounded-2xl shadow-2xl animate-[float_5s_ease-in-out_infinite_1s]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                    <GlobeIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-mono text-muted-foreground">Global Node</p>
                    <p className="text-sm font-serif font-bold text-white">Active</p>
                  </div>
                </div>
              </div>

              <div className="absolute bottom-20 -right-4 md:-right-8 bg-card/40 backdrop-blur-xl border border-white/10 p-4 rounded-2xl shadow-2xl animate-[float_7s_ease-in-out_infinite_2s]">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <p className="text-sm font-mono text-white">Status: Connected</p>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>;
}